import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  CartoucheSequencageCommandeeItem,
  CartouchesService,
} from '../../../services/cartouches.service';

@Component({
  selector: 'app-cartouches-commandees',
  imports: [CommonModule, FormsModule],
  templateUrl: './cartouches-commandees.component.html',
  styleUrl: './cartouches-commandees.component.scss',
})
export class CartouchesCommandeesComponent implements OnInit {
  protected loading = false;
  protected error = '';
  protected success = '';
  protected items: CartoucheSequencageCommandeeItem[] = [];
  protected dateByNom: Record<string, string> = {};
  protected savingByNom: Record<string, boolean> = {};

  constructor(
    private readonly cartouchesService: CartouchesService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.load();
  }

  protected refresh(): void {
    this.load();
  }

  protected async markAsNotCommanded(item: CartoucheSequencageCommandeeItem): Promise<void> {
    const nom = item.nom;
    if (this.savingByNom[nom]) {
      return;
    }

    this.savingByNom[nom] = true;
    this.error = '';
    this.success = '';
    this.cdr.markForCheck();

    this.cartouchesService
      .updateSequencageBatch({
        cartouche_noms: [nom],
        sequencage_commande: false,
        date_sequencage: null,
      })
      .subscribe({
        next: () => {
          this.success = `Cartouche ${nom} marquée non commandée.`;
          this.savingByNom[nom] = false;
          this.load();
        },
        error: (err: HttpErrorResponse) => {
          this.error = this.getErrorMessage(err);
          this.savingByNom[nom] = false;
          this.cdr.markForCheck();
        },
      });
  }

  protected async saveDate(item: CartoucheSequencageCommandeeItem): Promise<void> {
    const nom = item.nom;
    if (this.savingByNom[nom]) {
      return;
    }

    const date = (this.dateByNom[nom] ?? '').trim();
    if (!date) {
      this.error = `Veuillez saisir une date pour ${nom}.`;
      this.success = '';
      this.cdr.markForCheck();
      return;
    }

    this.savingByNom[nom] = true;
    this.error = '';
    this.success = '';
    this.cdr.markForCheck();

    this.cartouchesService
      .updateSequencageBatch({
        cartouche_noms: [nom],
        sequencage_commande: true,
        date_sequencage: date,
      })
      .subscribe({
        next: () => {
          this.success = `Date de séquençage enregistrée pour ${nom}.`;
          this.savingByNom[nom] = false;
          this.load();
        },
        error: (err: HttpErrorResponse) => {
          this.error = this.getErrorMessage(err);
          this.savingByNom[nom] = false;
          this.cdr.markForCheck();
        },
      });
  }

  private load(): void {
    this.loading = true;
    this.error = '';
    this.success = '';
    this.cdr.markForCheck();

    this.cartouchesService.listSequencageCommandees().subscribe({
      next: (items: CartoucheSequencageCommandeeItem[]) => {
        this.items = items;
        this.dateByNom = {};
        for (const item of this.items) {
          this.dateByNom[item.nom] = item.date_sequencage ?? '';
        }
        this.loading = false;
        this.cdr.markForCheck();
      },
      error: (err: HttpErrorResponse) => {
        this.error = this.getErrorMessage(err);
        this.items = [];
        this.loading = false;
        this.cdr.markForCheck();
      },
    });
  }

  private getErrorMessage(err: HttpErrorResponse): string {
    if (typeof err.error === 'string' && err.error.trim()) {
      return err.error;
    }
    if (err.error?.detail) {
      return String(err.error.detail);
    }
    if (err.error?.message) {
      return String(err.error.message);
    }
    return err.statusText || 'Erreur inconnue';
  }
}
